import * as React from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '../../utils/cn';

const badgeVariants = cva(
    'inline-flex items-center justify-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
    {
        variants: {
            variant: {
                default:
                    'border-transparent bg-primary text-primary-foreground',
                secondary:
                    'border-transparent bg-secondary text-secondary-foreground',
                destructive:
                    'border-transparent bg-destructive text-destructive-foreground',
                outline: 'bg-background border-border text-foreground',
            },
            interactive: {
                true: 'transition-colors focus:outline-none focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-0 cursor-pointer',
                false: '',
            },
        },
        defaultVariants: {
            variant: 'default',
            interactive: false,
        },
        compoundVariants: [
            {
                variant: 'default',
                interactive: true,
                className: 'hover:bg-[var(--badge-primary-hover)]',
            },
            {
                variant: 'secondary',
                interactive: true,
                className: 'hover:bg-gray-200 dark:hover:bg-gray-700',
            },
            {
                variant: 'destructive',
                interactive: true,
                className:
                    'hover:bg-[var(--badge-destructive-hover)] focus-visible:ring-[var(--ring-destructive)]',
            },
            {
                variant: 'outline',
                interactive: true,
                className: 'hover:bg-accent hover:text-accent-foreground',
            },
        ],
    },
);

const Badge = React.forwardRef(
    ({ className, variant, interactive, children, ...props }, ref) => {
        const Comp = interactive ? 'button' : 'div';

        return (
            <Comp
                ref={ref}
                className={cn(
                    badgeVariants({ variant, interactive }),
                    className,
                )}
                {...props}
            >
                {children}
            </Comp>
        );
    },
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
